import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

// Landing for the email-confirmation link (/portal/verify-email?token=...).
// Confirms the address, then bounces to the dashboard. This does NOT log the
// user in (the token only stamps email_verified_at) — verifyEmail refreshes the
// current session user so the banner disappears if they're already signed in.
export default function ClientVerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const { verifyEmail } = useClientAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // one-time-use token: never fire twice (StrictMode)
    ran.current = true;

    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setError(t('portal.verifyEmail.noToken', 'Falta el token de confirmación.'));
      return;
    }

    verifyEmail(token)
      .then(() => {
        setStatus('success');
        setTimeout(() => navigate('/portal/dashboard'), 1500);
      })
      .catch(err => {
        setStatus('error');
        setError(err instanceof Error ? err.message : t('portal.verifyEmail.invalid', 'Enlace inválido o caducado.'));
      });
  }, [searchParams, verifyEmail, navigate, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-teal-500 animate-spin mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">{t('portal.verifyEmail.checking', 'Confirmando tu email…')}</h2>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-teal-100 flex items-center justify-center mb-6">
              <CheckCircle className="text-teal-600" size={32} />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">{t('portal.verifyEmail.doneTitle', 'Email confirmado')}</h2>
            <p className="text-slate-600">{t('portal.verifyEmail.doneDesc', 'Tu cuenta está totalmente activada.')}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-rose-100 flex items-center justify-center mb-6">
              <XCircle className="text-rose-600" size={32} />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">{t('portal.verifyEmail.errorTitle', 'No se pudo confirmar')}</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/portal/dashboard')}
              className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors"
            >
              {t('portal.verifyEmail.goToDashboard', 'Ir al panel')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
